import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Chip,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  useTheme,
  useMediaQuery,
  Paper,
  Fab,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout,
  Settings,
  Person,
  MoreVert as MoreVertIcon,
  ExitToApp as ExitImpersonationIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';
import { getCurrentUserRole } from '../api/userManagement';
import ThemeToggle from './ThemeToggle';
import { useAccount } from '../contexts/AccountContext';

interface TopBarProps {
  onMobileMenuClick: () => void;
  sidebarOpen: boolean;
}

const TopBar: React.FC<TopBarProps> = ({ onMobileMenuClick, sidebarOpen }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Impersonation context
  const { isImpersonating, impersonatedUser, exitImpersonation } = useAccount();

  useEffect(() => {
    const fetchUser = async () => {
      const {
        data: { user },
        error,
      } = await supabase.auth.getUser();

      if (user) {
        setUserEmail(user.email ?? '');

        // Fetch user role
        const { role } = await getCurrentUserRole();
        setUserRole(role);
      }
      if (error) {
        console.error('Error getting user:', error.message);
      }
    };

    fetchUser();
  }, []);

  const handleLogout = async () => {
    setAnchorEl(null);
    await supabase.auth.signOut();
    navigate('/login');
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSettings = () => {
    setAnchorEl(null);
    navigate('/settings');
  };

  return (
    <>
      {/* Impersonation Banner - Shows when admin is impersonating a user */}
      {isImpersonating && impersonatedUser && (
        <Paper
          elevation={4}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.modal + 1,
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.warning.contrastText,
            py: 1,
            px: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                IMPERSONATING USER:
              </Typography>
              <Typography variant="body2">
                {impersonatedUser.first_name && impersonatedUser.last_name
                  ? `${impersonatedUser.first_name} ${impersonatedUser.last_name} (${impersonatedUser.email})`
                  : impersonatedUser.email
                }
              </Typography>
            </Box>

            <Button
              variant="outlined"
              size="small"
              onClick={exitImpersonation}
              startIcon={<ExitImpersonationIcon />}
              sx={{
                color: theme.palette.warning.contrastText,
                borderColor: theme.palette.warning.contrastText,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: theme.palette.warning.contrastText,
                }
              }}
            >
              Exit Impersonation
            </Button>
          </Box>
        </Paper>
      )}

      {/* Minimal AppBar for mobile only */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'transparent',
          boxShadow: 'none',
          display: isMobile ? 'block' : 'none',
          top: isImpersonating && impersonatedUser ? '48px' : '0px', // Adjust for impersonation banner
        }}
      >
        <Toolbar sx={{ justifyContent: 'flex-start', minHeight: '56px !important' }}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={onMobileMenuClick}
            size="small"
            sx={{
              p: 1,
              backgroundColor: 'rgba(160, 171, 96, 0.1)',
              border: '1px solid rgba(160, 171, 96, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(160, 171, 96, 0.2)',
              }
            }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>

          {/* Mobile user menu - positioned at the right */}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <ThemeToggle />

            <IconButton
              onClick={handleMenuOpen}
              size="small"
              sx={{
                p: 1,
                backgroundColor: 'rgba(160, 171, 96, 0.1)',
                border: '1px solid rgba(160, 171, 96, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(160, 171, 96, 0.2)',
                }
              }}
            >
              <AccountCircle fontSize="small" />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile User Menu */}
      {isMobile && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem disabled>
            <ListItemIcon>
              <Person />
            </ListItemIcon>
            <Box>
              <Typography variant="body2">{userEmail}</Typography>
              {userRole && (
                <Chip
                  label={userRole}
                  size="small"
                  color={userRole === 'admin' ? 'primary' : 'default'}
                  variant="outlined"
                />
              )}
            </Box>
          </MenuItem>

          <Divider />

          <MenuItem onClick={handleSettings}>
            <ListItemIcon>
              <Settings />
            </ListItemIcon>
            Settings
          </MenuItem>

          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <Logout />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      )}
    </>
  );
};

export default TopBar;
